// Light / dark / system (SPEC §7). The tokens are already paired under
// [data-scheme]; this only stores the choice (oase-admin:scheme, ADR 0017)
// and sets the attribute. "system" removes it, so prefers-color-scheme rules.
import { useSyncExternalStore } from 'react'

export type ColorScheme = 'system' | 'light' | 'dark'

export const SCHEME_KEY = 'oase-admin:scheme'

const listeners = new Set<() => void>()

function read(): ColorScheme {
  try {
    const value = localStorage.getItem(SCHEME_KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

let current: ColorScheme = typeof window === 'undefined' ? 'system' : read()

function apply(scheme: ColorScheme) {
  const root = document.documentElement
  if (scheme === 'system') root.removeAttribute('data-scheme')
  else root.setAttribute('data-scheme', scheme)
}

/** Apply the stored choice; call once at boot, before rendering, to avoid a flash. */
export function applyStoredScheme() {
  current = read()
  apply(current)
}

export function setColorScheme(scheme: ColorScheme) {
  current = scheme
  try {
    if (scheme === 'system') localStorage.removeItem(SCHEME_KEY)
    else localStorage.setItem(SCHEME_KEY, scheme)
  } catch {
    // storage unavailable: the choice lasts for this page
  }
  apply(scheme)
  listeners.forEach((listener) => {
    listener()
  })
}

export function useColorScheme(): ColorScheme {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
    () => 'system'
  )
}
